import { PanelSide } from './PanelSide';
import { TEMPLATES } from '../constants/reactionScheme';
import type { TemplateMode } from './BatchUpload';
import { useFileImport, type AlchemieImportResult } from '../hooks/useFileImport';
import { SchemeZoomControls } from './SchemeZoomControls';
import {
  AspectRatioLockIcon,
  Button,
  ClipboardIcon,
  FolderIcon,
  NumberScrubField,
  ResetIcon,
} from '@alchemiesolns/ui';

interface BuildSectionProps {
  // Template state
  templateMode: TemplateMode;
  onTemplateChange: (mode: TemplateMode) => void;

  // Molecule configs
  reactantConfig: string;
  productConfig: string;
  onReactantConfigChange: (config: string) => void;
  onProductConfigChange: (config: string) => void;

  // Text overlay state
  reactantOverlayText?: string;
  productOverlayText?: string;
  onAddReactantText?: () => void;
  onAddProductText?: () => void;

  // Remove config handlers
  onRemoveReactantConfig?: () => void;
  onRemoveProductConfig?: () => void;

  // Remove text handlers
  onRemoveReactantText?: () => void;
  onRemoveProductText?: () => void;

  // Molecule transfer (reaction mode only)
  moleculeTransfer: {
    transferReactantToProduct: () => void;
    transferProductToReactant: () => void;
  };

  // Sketcher modal
  sketcherModal: {
    editMolecule: () => void;
    editReactant: () => void;
    editProduct: () => void;
    editMoleculeExisting: () => void;
    editReactantExisting: () => void;
    editProductExisting: () => void;
  };

  onImportError: (msg: string) => void;
  onAlchemieImport: (result: AlchemieImportResult) => void;
  /** Clear reactant, product, arrow, overlays, and selection (template mode unchanged). */
  onResetBuild: () => void;

  // Sizing controls
  exportWidth: number;
  exportHeight: number;
  aspectLock: boolean;
  onExportWidthChange: (width: number) => void;
  onExportHeightChange: (height: number) => void;
  onAspectLockChange: (locked: boolean) => void;

  schemeZoomPercent: number;
  onSchemeZoomPercentChange: (percent: number) => void;
  /** After scrub end or zoom field commit. */
  onSchemeZoomSettled: (
    percentCommitted: number,
    before: { percent: number },
  ) => void;

  /** Reaction template: show slot outlines on the build preview. */
  showMoleculeBounds?: boolean;
  onToggleMoleculeBounds?: () => void;
}

export function BuildSection({
  templateMode,
  onTemplateChange,
  reactantConfig,
  productConfig,
  onReactantConfigChange,
  onProductConfigChange,
  reactantOverlayText,
  productOverlayText,
  onAddReactantText,
  onAddProductText,
  onRemoveReactantConfig,
  onRemoveProductConfig,
  onRemoveReactantText,
  onRemoveProductText,
  moleculeTransfer,
  sketcherModal,
  onImportError,
  onAlchemieImport,
  onResetBuild,
  exportWidth,
  exportHeight,
  aspectLock,
  onExportWidthChange,
  onExportHeightChange,
  onAspectLockChange,
  schemeZoomPercent,
  onSchemeZoomPercentChange,
  onSchemeZoomSettled,
  showMoleculeBounds = false,
  onToggleMoleculeBounds,
}: BuildSectionProps) {
  const schemeFileBrowse = useFileImport({
    onConfigImport: onReactantConfigChange,
    onAlchemieImport,
    onImportError,
  });

  return (
    <div className="build-section">
      <div className="build-side-card build-layout-card build-file-card">
        <h4 className="panel-side-title build-file-card-title">File</h4>
        <div className="build-file-actions">
          <Button
            className="build-file-action-btn"
            onClick={() => schemeFileBrowse.browseFiles()}
            aria-label="Open file to import a saved scheme or CDXML molecule"
          >
            <FolderIcon width={16} height={16} />
            Open
          </Button>
          <Button
            className="build-file-action-btn"
            onClick={onResetBuild}
            aria-label="Reset reactant, product, and arrow"
          >
            <ResetIcon width={16} height={16} />
            Reset
          </Button>
        </div>
        <label className="template-label" htmlFor="scheme-template-select">
          Template
        </label>
        <select
          id="scheme-template-select"
          value={templateMode}
          onChange={(e) => onTemplateChange(e.target.value as TemplateMode)}
          className="template-dropdown"
        >
          {TEMPLATES.map((template) => (
            <option key={template.id} value={template.id}>
              {template.label}
            </option>
          ))}
        </select>
      </div>

      {/* Resize (dimensions & zoom) — structure differs by template mode */}
      {templateMode === 'molecule' ? (
        <>
          <div className="build-side-card build-layout-card">
            <h4 className="panel-side-title build-layout-card-title">Resize</h4>
            <div className="build-preview-controls-row">
              <div className="build-size-input-group">
                <NumberScrubField
                  label="Width"
                  value={exportWidth}
                  onChange={onExportWidthChange}
                  min={64}
                  max={8192}
                  unit="px"
                />
              </div>
              <div className="build-size-input-group">
                <NumberScrubField
                  label="Height"
                  value={exportHeight}
                  onChange={onExportHeightChange}
                  min={64}
                  max={8192}
                  unit="px"
                />
              </div>
              <button
                type="button"
                className={`build-aspect-ratio-toggle${aspectLock ? ' build-aspect-ratio-toggle--active' : ''}`}
                onClick={() => onAspectLockChange(!aspectLock)}
                aria-pressed={aspectLock}
                title={aspectLock ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                aria-label={aspectLock ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
              >
                <AspectRatioLockIcon width={18} height={18} active={aspectLock} />
              </button>
            </div>
            <div className="build-layout-scheme-zoom">
              <SchemeZoomControls
                zoomPercent={schemeZoomPercent}
                onZoomPercentChange={onSchemeZoomPercentChange}
                onZoomSettled={onSchemeZoomSettled}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Display section temporarily disabled */}
          {false && (
            <div className=" build-display-card">
              <h4 className="panel-side-title build-display-card-title">Display</h4>
              <div className="build-panel-sides-row single">
                <PanelSide
                  label="Reactant"
                  extraButton={
                    <button
                      onClick={moleculeTransfer.transferProductToReactant}
                      className="transfer-button"
                      aria-label="Transfer product to reactant"
                      title="Copy from product"
                    >
                      <ClipboardIcon />
                    </button>
                  }
                  onCreateSVG={sketcherModal.editReactant}
                  onAddText={() => onAddReactantText?.()}
                  configString={reactantConfig}
                  onRemoveConfig={onRemoveReactantConfig}
                  onEditExisting={sketcherModal.editReactantExisting}
                  overlayText={reactantOverlayText}
                  onRemoveText={onRemoveReactantText}
                  useInlineToolbar
                />
                <PanelSide
                  label="Product"
                  extraButton={
                    <button
                      onClick={moleculeTransfer.transferReactantToProduct}
                      className="transfer-button"
                      aria-label="Transfer reactant to product"
                      title="Copy from reactant"
                    >
                      <ClipboardIcon />
                    </button>
                  }
                  onCreateSVG={sketcherModal.editProduct}
                  onAddText={() => onAddProductText?.()}
                  configString={productConfig}
                  onRemoveConfig={onRemoveProductConfig}
                  onEditExisting={sketcherModal.editProductExisting}
                  overlayText={productOverlayText}
                  onRemoveText={onRemoveProductText}
                  useInlineToolbar
                />
              </div>
            </div>
          )}
          <div className="build-side-card build-layout-card">
            <h4 className="panel-side-title build-layout-card-title">Resize</h4>
            <div className="build-preview-controls-stack">
              <div className="build-preview-controls-row">
                <div className="build-size-input-group">
                  <NumberScrubField
                    label="Width"
                    value={exportWidth}
                    onChange={onExportWidthChange}
                    min={64}
                    max={8192}
                    unit="px"
                  />
                </div>
                <div className="build-size-input-group">
                  <NumberScrubField
                    label="Height"
                    value={exportHeight}
                    onChange={onExportHeightChange}
                    min={64}
                    max={8192}
                    unit="px"
                  />
                </div>
                {/* {templateMode === 'reaction' && onToggleMoleculeBounds && (
                  <button
                    type="button"
                    className={`build-molecule-bounds-toggle${showMoleculeBounds ? ' build-molecule-bounds-toggle--active' : ''}`}
                    onClick={onToggleMoleculeBounds}
                    aria-pressed={showMoleculeBounds}
                    title={
                      showMoleculeBounds
                        ? 'Hide reactant and product molecule bounds'
                        : 'Show reactant and product molecule bounds'
                    }
                    aria-label={
                      showMoleculeBounds
                        ? 'Hide reactant and product molecule bounds'
                        : 'Show reactant and product molecule bounds'
                    }
                  >
                    Bounds
                  </button>
                )} */}
                <button
                  type="button"
                  className={`build-aspect-ratio-toggle${aspectLock ? ' build-aspect-ratio-toggle--active' : ''}`}
                  onClick={() => onAspectLockChange(!aspectLock)}
                  aria-pressed={aspectLock}
                  title={aspectLock ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                  aria-label={aspectLock ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                >
                  <AspectRatioLockIcon width={18} height={18} active={aspectLock} />
                </button>
              </div>
            </div>
            <div className="build-layout-scheme-zoom">
              <SchemeZoomControls
                zoomPercent={schemeZoomPercent}
                onZoomPercentChange={onSchemeZoomPercentChange}
                onZoomSettled={onSchemeZoomSettled}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
